/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-nested-ternary */
import {
  Avatar, Flex, Select, Text
} from "@mantine/core";
import React, {
  forwardRef,
  useCallback,
  useMemo, memo,
} from "react";
import { useWorkspaceMembers } from "../../hooks/QueryHooks";
import useWorkspaceStore from "../../store/workspace/workspaceStore";
import { FILTER_COMBOBOX_PROPS, FILTER_SELECT_CLASSES } from "./filterStyles";

type Props = {
  handleChange: (
    operator: string,
    value: any,
    type: string,
    entityType: string,
    selectedField?: any,
  ) => void;
  fieldSelect: boolean;
  op?: string;
  value?: any;
};

interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
  avatar_url?: string;
  email?: string;
  name: string;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({
    avatar_url, email, name, ...others
  }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Flex gap={8} align="center" className="overflow-hidden">
        <Avatar src={avatar_url} color="initials" radius="xl" size="sm" name={name} alt={name}>{name ? name[0] : ""}</Avatar>
        <div>
          <Text truncate size="sm" fw={500} lh={1.5}>{name}</Text>
          <Text truncate size="xs" lh={1} opacity={0.65}>
            {email}
          </Text>
        </div>
      </Flex>
    </div>
  ),
);

// Move constants outside component
const OPS = [
  { value: "eq", label: "is equal to", operator: "eq", id: 1 },
  { value: "ne", label: "is not equal to", operator: "ne", id: 2 },
];

const MemberFilter = memo((props: Props) => {
  const {
    op,
    value,
    handleChange,
    fieldSelect,
  } = props;

  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);

  const { data: workspaceMembers }: any = useWorkspaceMembers({
    workspaceId,
    enabled: !!workspaceId,
    retry: 2,
  });

  const csmList = useMemo(
    () => workspaceMembers?.users?.map((member: any) => ({
      value: member.user_id,
      label: `${member.first_name} ${member.last_name}`,
      name: `${member.first_name} ${member.last_name}`,
      email: member.email,
      avatar_url: member.avatar_url,
    })) || [],
    [workspaceMembers?.users]
  );

  // Fully controlled - derive from props
  const selectOp = op || null;
  const selectValue = value || "";

  // Call handleChange directly on user interaction
  const handleOpChange = useCallback((val: string | null) => {
    if (val && selectValue) {
      const operator = OPS.find(item => item.value === val)?.operator || val;
      handleChange(operator, selectValue, "string", "column");
    }
  }, [selectValue, handleChange]);

  const handleValueChange = useCallback((val: string | null) => {
    if (selectOp && val) {
      const operator = OPS.find(item => item.value === selectOp)?.operator || selectOp;
      handleChange(operator, val, "string", "column");
    }
  }, [selectOp, handleChange]);

  return (
    <>
      <Select
        disabled={fieldSelect}
        placeholder="Select Condition"
        data={OPS}
        value={selectOp}
        classNames={FILTER_SELECT_CLASSES}
        comboboxProps={FILTER_COMBOBOX_PROPS}
        onChange={handleOpChange}
        size="xs"
        w={150}
      />
      <Select
        value={selectValue}
        classNames={FILTER_SELECT_CLASSES}
        comboboxProps={FILTER_COMBOBOX_PROPS}
        placeholder="Enter value here"
        onChange={handleValueChange}
        data={csmList}
        searchable
        w={180}
        maxDropdownHeight={400}
        size="xs"
        selectFirstOptionOnChange={false}
        nothingFoundMessage="Nobody here"
        renderOption={(item: any) => (
          <SelectItem
            key={item.option.value}
            name={item.option.name}
            email={item.option.email}
            avatar_url={item.option.avatar_url}
          />
        )}
      />
    </>
  );
});

MemberFilter.displayName = "MemberFilter";

export default MemberFilter;
